import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, MapPin, Package, ShoppingBag, Phone, Mail, Edit2, Save, X, Gift, Copy, Wallet, Store, FileText, Clock, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

export default function CustomerDashboard() {
  const { user, setUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', street: user?.address?.street || '', city: user?.address?.city || '', state: user?.address?.state || '', pincode: user?.address?.pincode || '' });
  const [upiId, setUpiId] = useState(user?.referralPayout?.upiId || '');
  const [savingUpi, setSavingUpi] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/orders/my-orders').catch(() => ({ data: [] })),
      API.get('/quotes/mine').catch(() => ({ data: [] }))
    ])
      .then(([ordersRes, quotesRes]) => {
        setOrders(ordersRes.data || []);
        setQuotes(quotesRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const totalSpent = orders.filter(o => o.paymentStatus?.advance === 'paid').reduce((s, o) => s + (o.advanceAmount || 0), 0);
  const customerReferralLink = `${window.location.origin}/register?ref=${user?.referralCode || ''}`;
  const ownerReferralLink = `${window.location.origin}/register/owner?ref=${user?.referralCode || ''}`;
  const referralCredits = user?.referralCredits || [];
  const pendingReferralAmount = referralCredits
    .filter(ref => (ref.payoutStatus || 'pending') === 'pending')
    .reduce((sum, ref) => sum + (Number(ref.amount) || 0), 0);
  const paidReferralAmount = referralCredits
    .filter(ref => ref.payoutStatus === 'paid')
    .reduce((sum, ref) => sum + (Number(ref.amount) || 0), 0);
  const formatMoney = (value) => `Rs ${Number(value || 0).toLocaleString('en-IN')}`;

  const decideQuote = async (quoteId, decision) => {
    try {
      const res = await API.put(`/quotes/${quoteId}/decision`, { decision });
      setQuotes(current => current.map(quote => quote._id === quoteId ? { ...quote, status: res.data.status } : quote));
      toast.success(decision === 'accepted' ? 'Quote accepted. The supplier has been notified.' : 'Quote declined');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to update quote');
    }
  };

  const stats = [
    { label: 'Total Orders', value: orders.length, color: '#3B82F6' },
    { label: 'Delivered', value: orders.filter(o => o.status === 'delivered').length, color: 'var(--green)' },
    { label: 'Pending', value: orders.filter(o => ['pending', 'confirmed', 'processing'].includes(o.status)).length, color: '#F59E0B' },
    { label: 'Advance Paid', value: `₹${totalSpent.toLocaleString()}`, color: 'var(--orange)' },
    { label: 'Wallet', value: `₹${(user?.walletBalance || 0).toLocaleString()}`, color: 'var(--green)' },
  ];

  const copyReferral = async (type) => {
    await navigator.clipboard.writeText(type === 'owner' ? ownerReferralLink : customerReferralLink);
    toast.success(type === 'owner' ? 'Owner referral link copied' : 'Customer referral link copied');
  };

  const handleSave = async () => {
    try {
      const res = await API.put('/auth/me', { name: form.name, phone: form.phone, address: { street: form.street, city: form.city, state: form.state, pincode: form.pincode } });
      setUser(res.data.user);
      localStorage.setItem('ms_user', JSON.stringify(res.data.user));
      toast.success('Profile updated!');
      setEditing(false);
    } catch { toast.error('Update failed'); }
  };

  const saveUpiId = async () => {
    if (!upiId.trim()) {
      toast.error('Enter your UPI ID');
      return;
    }
    setSavingUpi(true);
    try {
      const res = await API.put('/auth/me', { referralPayout: { upiId } });
      setUser(res.data.user);
      localStorage.setItem('ms_user', JSON.stringify(res.data.user));
      toast.success('UPI ID saved');
    } catch {
      toast.error('Failed to save UPI ID');
    } finally {
      setSavingUpi(false);
    }
  };

  const referralSummaryCard = (
    <div className="customer-dashboard-card customer-referral-summary" style={{ background: 'white', borderRadius: 14, padding: 20, boxShadow: 'var(--shadow)', order: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <h3 style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gift size={16} color="var(--orange)" /> Referral Program
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--green)', fontWeight: 800 }}>
          <Wallet size={15} /> Wallet: {formatMoney(user?.walletBalance)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 12 }}>
        <div style={{ background: 'var(--orange-soft)', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 800, marginBottom: 3 }}>YOUR CODE</div>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: 1 }}>{user?.referralCode || 'Loading'}</div>
        </div>
        <div style={{ background: '#ECFDF5', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: 'var(--green)', fontWeight: 800, marginBottom: 3 }}>TOTAL EARNED</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(user?.referralStats?.totalEarnings)}</div>
        </div>
        <div style={{ background: '#FFFBEB', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: '#D97706', fontWeight: 800, marginBottom: 3 }}>PENDING PAYOUT</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(pendingReferralAmount)}</div>
        </div>
        <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, color: '#2563EB', fontWeight: 800, marginBottom: 3 }}>PAID</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{formatMoney(paidReferralAmount)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12, padding: '9px 12px', background: 'var(--gray-light)', borderRadius: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>
          <strong style={{ color: 'var(--dark)' }}>{user?.referralStats?.totalReferrals || referralCredits.length || 0}</strong> total referrals
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray)' }}>
          Customer: <strong style={{ color: 'var(--dark)' }}>{user?.referralStats?.customerReferrals || 0}</strong>
          <span style={{ margin: '0 7px' }}>·</span>
          Owner: <strong style={{ color: 'var(--dark)' }}>{user?.referralStats?.ownerReferrals || 0}</strong>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
        <button onClick={() => copyReferral('customer')} className="btn-primary" style={{ justifyContent: 'center', padding: '11px' }}>
          <Copy size={14} /> Refer Customer
        </button>
        <button onClick={() => copyReferral('owner')} className="btn-outline" style={{ justifyContent: 'center', padding: '9px' }}>
          <Copy size={14} /> Refer Owner
        </button>
      </div>
      <div style={{ color: 'var(--gray)', fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
        Share either link. Customer and owner rewards are controlled by admin settings.
      </div>
    </div>
  );

  const referralListCard = (
    <div className="customer-dashboard-card customer-referral-list" style={{ background: 'white', borderRadius: 14, padding: 20, boxShadow: 'var(--shadow)', order: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <h3 style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Gift size={16} color="var(--orange)" /> My Referrals
        </h3>
        <span style={{ background: 'var(--orange-soft)', color: 'var(--orange)', borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 800 }}>
          {referralCredits.length} Total
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 12 }}>
        <div style={{ background: '#FFFBEB', borderRadius: 10, padding: '9px 11px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#B45309' }}>PENDING</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>{formatMoney(pendingReferralAmount)}</div>
        </div>
        <div style={{ background: '#ECFDF5', borderRadius: 10, padding: '9px 11px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--green)' }}>PAID</div>
          <div style={{ fontSize: 18, fontWeight: 900 }}>{formatMoney(paidReferralAmount)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input className="form-input" placeholder="Enter UPI ID for referral payment"
          value={upiId}
          onChange={e => setUpiId(e.target.value)}
          style={{ flex: '1 1 220px' }} />
        <button onClick={saveUpiId} className="btn-primary" style={{ padding: '10px 14px', justifyContent: 'center' }} disabled={savingUpi}>
          <Save size={14} /> {savingUpi ? 'Saving...' : 'Save UPI'}
        </button>
      </div>

      {referralCredits.length === 0 ? (
        <div style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 18, textAlign: 'center' }}>
          <Gift size={28} color="var(--gray)" style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 800, fontSize: 14 }}>No referrals yet</div>
          <div style={{ color: 'var(--gray)', fontSize: 12, marginTop: 4 }}>Share your customer or owner link to start earning rewards.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {referralCredits.map(ref => {
            const isOwner = ref.referredRole === 'owner';
            const status = ref.payoutStatus || 'pending';
            return (
              <div key={ref._id || `${ref.referredPhone}-${ref.createdAt}`} style={{ display: 'grid', gridTemplateColumns: '38px minmax(0,1fr) auto', gap: 10, alignItems: 'center', background: 'var(--gray-light)', borderRadius: 12, padding: '10px 12px' }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: isOwner ? 'var(--orange-soft)' : '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isOwner ? <Store size={17} color="var(--orange)" /> : <User size={17} color="var(--green)" />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ref.referredName || ref.referredUser?.name || 'Referral User'}
                  </div>
                  <div style={{ color: 'var(--gray)', fontSize: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span>{ref.referredPhone || ref.referredUser?.phone || 'No phone'}</span>
                    <span style={{ textTransform: 'capitalize' }}>{ref.referredRole}</span>
                    {ref.createdAt && <span>{new Date(ref.createdAt).toLocaleDateString('en-IN')}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ display: 'inline-block', marginBottom: 3, background: status === 'paid' ? '#ECFDF5' : '#FFFBEB', color: status === 'paid' ? 'var(--green)' : '#B45309', borderRadius: 20, padding: '2px 7px', fontSize: 10, fontWeight: 900 }}>
                    {status.toUpperCase()}
                  </span>
                  <div style={{ fontWeight: 900, color: 'var(--green)', fontSize: 14 }}>{formatMoney(ref.amount)}</div>
                  <div style={{ color: 'var(--gray)', fontSize: 10 }}>Reward</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const customerQuotesCard = (
    <div className="customer-dashboard-card customer-quotes-card">
      <div className="customer-quotes-header">
        <div><FileText size={17} /><div><span>Supplier pricing</span><h3>My Quotes</h3></div></div>
        <span>{quotes.length} Total</span>
      </div>
      {quotes.length === 0 ? (
        <div className="customer-quotes-empty"><FileText size={30} /><strong>No quote requests yet</strong><span>Request custom pricing from any material card.</span><Link to="/shops?view=materials">Browse materials <ArrowRight size={13} /></Link></div>
      ) : (
        <div className="customer-quotes-list">
          {quotes.slice(0, 4).map(quote => {
            const QuoteIcon = quote.status === 'quoted' || quote.status === 'accepted' ? CheckCircle : quote.status === 'rejected' || quote.status === 'declined' ? XCircle : Clock;
            return (
              <article className={`customer-quote-row status-${quote.status}`} key={quote._id}>
                <div className="customer-quote-icon"><QuoteIcon size={17} /></div>
                <div className="customer-quote-main">
                  <div><Link to={`/materials/${quote.material?._id}`}>{quote.material?.name || 'Material'}</Link><span>{quote.status}</span></div>
                  <p>{quote.owner?.shopName || 'Supplier'} / {quote.quantity} {quote.unit}{quote.variant ? ` / ${quote.variant}` : ''}</p>
                  {quote.ownerMessage && <small>{quote.ownerMessage}</small>}
                </div>
                <div className="customer-quote-side">
                  {quote.status === 'quoted' ? <strong>{formatMoney(quote.quotedPrice)}</strong> : <strong>{new Date(quote.createdAt).toLocaleDateString('en-IN')}</strong>}
                  {quote.deliveryDays !== undefined && quote.status !== 'pending' && <span>{quote.deliveryDays} day delivery</span>}
                  {quote.status === 'quoted' && <div><button onClick={() => decideQuote(quote._id, 'declined')}>Decline</button><button onClick={() => decideQuote(quote._id, 'accepted')}>Accept</button></div>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--page-bg)" }} className="page-with-bottom-nav">
      <Navbar />
      <div className="customer-dashboard-shell" style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 20px' }}>
        <div className="customer-dashboard-heading">
          <div>
            <span>Customer account</span>
            <h1>Welcome back, {user?.name?.split(' ')[0] || 'Customer'}</h1>
            <p>Track orders, manage your delivery details, and view referral rewards.</p>
          </div>
          <Link to="/shops?view=materials" className="btn-primary"><Package size={16} /> Shop materials</Link>
        </div>

        <div className="customer-dashboard-layout" style={{ display: 'grid', gridTemplateColumns: '340px minmax(0,1fr)', gap: 20, alignItems: 'start' }}>
          {/* Profile Card */}
          <div className="customer-profile-card" style={{ background: 'white', borderRadius: 16, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
            <div className="customer-profile-head" style={{ background: 'linear-gradient(135deg,var(--orange),var(--amber))', padding: '28px 20px', textAlign: 'center' }}>
              <div style={{ width: 70, height: 70, background: 'rgba(255,255,255,0.25)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 28, fontWeight: 700, color: 'white', fontFamily: 'var(--font-main)' }}>{user?.name?.[0]?.toUpperCase()}</div>
              <div style={{ color: 'white', fontWeight: 700, fontSize: 18, fontFamily: 'var(--font-main)' }}>{user?.name}</div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>Customer</div>
            </div>
            <div style={{ padding: 20 }}>
              {!editing ? (
                <>
                  {[{ icon: Mail, val: user?.email }, { icon: Phone, val: user?.phone }, { icon: MapPin, val: [user?.address?.street, user?.address?.city, user?.address?.state, user?.address?.pincode].filter(Boolean).join(', ') || 'No address set' }].map(({ icon: Icon, val }) => (
                    <div key={val} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
                      <Icon size={15} color="var(--orange)" style={{ marginTop: 2, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.5 }}>{val}</span>
                    </div>
                  ))}
                  <button onClick={() => setEditing(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center', padding: '10px', borderRadius: 8, border: '1.5px solid var(--gray-border)', background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginTop: 8 }}>
                    <Edit2 size={13} /> Edit Profile
                  </button>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label className="form-label">Full Name</label>
                    <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Phone</label>
                    <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className="form-label">Street</label>
                    <input className="form-input" placeholder="Street / Area" value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="form-input" placeholder="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                    <input className="form-input" placeholder="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
                  </div>
                  <input className="form-input" placeholder="Pincode" value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={() => setEditing(false)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '9px', borderRadius: 8, border: '1.5px solid var(--gray-border)', background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}><X size={13} /> Cancel</button>
                    <button onClick={handleSave} className="btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '9px' }}><Save size={13} /> Save</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right side */}
          <div className="customer-dashboard-main" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Stats */}
            <div className="customer-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12 }}>
              {stats.map((s, i) => (
                <div className="customer-stat-card" key={i} style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: 'var(--shadow)', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: 'var(--font-main)', marginBottom: 4 }}>
                    {s.label === 'Advance Paid' ? formatMoney(totalSpent) : s.label === 'Wallet' ? formatMoney(user?.walletBalance) : s.value}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray)' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="customer-dashboard-card customer-quick-card" style={{ background: 'white', borderRadius: 14, padding: 20, boxShadow: 'var(--shadow)' }}>
              <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Quick Actions</h3>
              <div className="customer-quick-actions" style={{ display: 'flex', gap: 10 }}>
                <Link to="/shops" className="btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '11px' }}>
                  <Package size={15} /> Browse Materials
                </Link>
                <Link to="/orders" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px', borderRadius: 8, border: '1.5px solid var(--gray-border)', background: 'white', color: 'var(--dark)', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                  <ShoppingBag size={15} /> My Orders
                </Link>
              </div>
            </div>

            {customerQuotesCard}
            {referralSummaryCard}
            {referralListCard}

            {/* Recent Orders */}
            <div className="customer-dashboard-card customer-recent-orders" style={{ background: 'white', borderRadius: 14, padding: 20, boxShadow: 'var(--shadow)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ fontWeight: 700, fontSize: 16 }}>Recent Orders</h3>
                <Link to="/orders" style={{ color: 'var(--orange)', fontSize: 13, fontWeight: 600 }}>View all</Link>
              </div>
              {loading ? <div className="spinner" style={{ margin: '0 auto' }} /> :
                orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--gray)', fontSize: 14 }}>No orders yet. <Link to="/shops" style={{ color: 'var(--orange)' }}>Start shopping</Link></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {orders.slice(0, 4).map(order => (
                      <div key={order._id} className="customer-order-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 14px', background: 'var(--gray-light)', borderRadius: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{order.owner?.shopName || 'Shop'}</div>
                          <div style={{ color: 'var(--gray)', fontSize: 12 }}>{new Date(order.createdAt).toLocaleDateString('en-IN')} · {order.items?.length} items</div>
                        </div>
                        <div className="customer-order-total" style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>₹{order.totalAmount?.toLocaleString()}</div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: order.status === 'delivered' ? 'var(--green)' : order.status === 'cancelled' ? 'var(--red)' : 'var(--orange)' }}>
                            {order.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
